import { FLEET, isHiddenCabinet, normalizeCabinetPayload } from '$lib/fleet';
import type { SupplyPoint } from '$lib/fleetModes/types';
import { getSupabase } from '$lib/supabase';

export const CABINET_STATE_API = FLEET.CABINET_API;



export interface BatterySlot {
  slot?: number;
  soc?: number;
  temp?: number;
  voltage?: number;
  charging?: boolean;
  empty?: boolean;
  canId?: string;
  soh?: number;
}

export interface RawCabinetDoor {
  slot: number;
  batteryPresent?: boolean;
  batterySn?: string | null;
  batteryId?: string | null;
  statusCode?: number;
  doorClosed?: boolean;
  soc?: number | null;
  soh?: number | null;
  temperature?: number | null;
  cellCount?: number;
  minCellMv?: number | null;
  maxCellMv?: number | null;
  cellImbalanceMv?: number | null;
  status?: string | null;
}

export interface RawCabinetState {
  cabinet_id: string;
  cabinet_name?: string | null;
  lastConnectedRaw?: string;
  lastConected?: string;
  totalSlots?: number;
  batteriesPresent?: number;
  batteriesCharging?: number;
  batteriesFull?: number;
  serialNumber?: string;
  cabinetLocation?: string | null;
  location?: string | null;
  capacity?: number | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  doors?: RawCabinetDoor[] | null;
  last_log_time?: string | null;
}

export interface CabinetBattery {
  cabinet_id: string;
  display_name: string;
  total_slots?: number;
  filled_slots?: number;
  empty_slots?: number;
  avg_soc?: number;
  batteries?: BatterySlot[];
  device_status?: number;
  seen_at?: string;
}

export interface CabinetState extends CabinetBattery {
  cabinet_id: string;
  display_name: string;
  serial_number?: string;
  cabinetLocation?: string | null;
  location?: string | null;
  capacity?: number | null;
  lat: number | null;
  lng: number | null;
  total_slots: number;
  filled_slots: number;
  empty_slots: number;
  batteries_charging: number;
  batteries_full: number;
  avg_soc: number;
  /** Number of present doors with a real SOC register (0 means unknown). */
  known_soc_count: number;
  last_connected: string;
  last_connected_raw: string;
  is_online: boolean;
  doors: RawCabinetDoor[];
  batteries: BatterySlot[];
  device_status: number;
  seen_at: string;
}

/**
 * Sanity-check and normalize coordinates from cabinet telemetry.
 * Kenyan latitudes come back as positive numbers in some upstream feeds (e.g. 1.28 for Nairobi at -1.28).
 * Southern-hemisphere values in 0..5 are flipped south.
 */
export function parseCabinetCoords(
  rawLat: unknown,
  rawLng: unknown,
): { lat: number; lng: number } | null {
  if (rawLat == null || rawLng == null) return null;
  let lat = typeof rawLat === 'number' ? rawLat : Number.parseFloat(String(rawLat).trim());
  const lng = typeof rawLng === 'number' ? rawLng : Number.parseFloat(String(rawLng).trim());
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  if (lat > 0 && lat < 5) lat = -lat;
  // Kenya bbox: lat [-5, 5], lng [33, 42]
  if (lat < -5 || lat > 5 || lng < 33 || lng > 42) return null;
  return { lat, lng };
}

function normalizeKey(str: string | null | undefined): string {
  return (str || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function parseDoorStatusCode(status: string | null | undefined): number {
  if (status === 'charging') return 1;
  if (status === 'ready' || status === 'full') return 2;
  if (status === 'fault') return 5;
  return 0;
}

export function parseRawCabinetDoor(door: unknown, fallbackSlot: number): RawCabinetDoor {
  const d = (door && typeof door === 'object' ? door : {}) as Record<string, unknown>;
  const status = typeof d.status === 'string' ? d.status : null;
  const batterySn =
    typeof d.battery_sn === 'string'
      ? d.battery_sn
      : typeof d.batterySn === 'string'
        ? d.batterySn
        : null;
  const batteryId =
    typeof d.battery_id === 'string'
      ? d.battery_id
      : typeof d.batteryId === 'string'
        ? d.batteryId
        : null;
  const batteryPresent =
    typeof d.batteryPresent === 'boolean'
      ? d.batteryPresent
      : Boolean(batterySn || batteryId || d.battery_sn || d.batterySn);

  return {
    slot: typeof d.slot === 'number' ? d.slot : fallbackSlot,
    batteryPresent,
    batterySn,
    batteryId,
    statusCode: typeof d.statusCode === 'number' ? d.statusCode : parseDoorStatusCode(status),
    doorClosed: typeof d.doorClosed === 'boolean' ? d.doorClosed : undefined,
    soc: typeof d.soc === 'number' ? d.soc : null,
    soh: typeof d.soh === 'number' ? d.soh : null,
    temperature: typeof d.temperature === 'number' ? d.temperature : null,
    cellCount: typeof d.cellCount === 'number' ? d.cellCount : undefined,
    minCellMv: typeof d.minCellMv === 'number' ? d.minCellMv : null,
    maxCellMv: typeof d.maxCellMv === 'number' ? d.maxCellMv : null,
    cellImbalanceMv: typeof d.cellImbalanceMv === 'number' ? d.cellImbalanceMv : null,
    status,
  };
}

export function parseRawCabinetDoors(rawDoors: unknown[]): RawCabinetDoor[] {
  return rawDoors.map((door, index) => parseRawCabinetDoor(door, index + 1));
}

export function isCabinetOffline(lastConected?: string | null, lastSeen?: string | null): boolean {
  const lastConStr = lastConected || '';
  const lastSeenMs = lastSeen ? Date.parse(lastSeen) : Number.NaN;
  return (
    lastConStr.includes('hour') ||
    lastConStr.includes('day') ||
    lastConStr.includes('week') ||
    lastConStr.includes('month') ||
    (!lastConStr && Number.isFinite(lastSeenMs) && Date.now() - lastSeenMs > 30 * 60_000)
  );
}

export function transformRawCabinet(raw: RawCabinetState): CabinetState {
  const coords = parseCabinetCoords(raw.latitude, raw.longitude);
  const doors = Array.isArray(raw.doors) ? raw.doors : [];
  const totalSlots = raw.totalSlots ?? raw.capacity ?? (doors.length > 0 ? doors.length : 8);

  const batteries: BatterySlot[] = [];
  let filledCount = 0;
  let chargingCount = 0;
  let fullCount = 0;
  let totalSoc = 0;
  let knownSocCount = 0;

  for (let i = 0; i < totalSlots; i++) {
    const d = doors[i];
    if (d) {
      const isPresent = d.batteryPresent ?? Boolean(d.batterySn);
      const soc =
        typeof d.soc === 'number' ? Math.max(0, Math.min(100, Math.round(d.soc))) : undefined;
      // The cabinet status register is authoritative.  SOC is a measurement,
      // not a charging-state substitute (a ready pack may report 59% while a
      // charger is paused, and a charging pack can briefly report 95%).
      const status = d.status?.trim().toLowerCase();
      const isCharging = isPresent && (d.statusCode === 1 || status === 'charging');
      const isFull = isPresent && (d.statusCode === 2 || status === 'ready' || status === 'full');

      if (isPresent) {
        filledCount++;
        if (soc !== undefined) {
          totalSoc += soc;
          knownSocCount++;
        }
        if (isCharging) chargingCount++;
        if (isFull) fullCount++;
      }

      batteries.push({
        slot: d.slot ?? i + 1,
        soc,
        temp: typeof d.temperature === 'number' ? d.temperature : undefined,
        voltage:
          typeof d.maxCellMv === 'number' && d.cellCount
            ? (d.cellCount * d.maxCellMv) / 1000
            : undefined,
        charging: isCharging,
        empty: !isPresent,
        canId: d.batterySn ?? undefined,
        soh: typeof d.soh === 'number' ? d.soh : undefined,
      });
    } else {
      batteries.push({
        slot: i + 1,
        soc: 0,
        empty: true,
        charging: false,
      });
    }
  }

  const effectiveFilled = raw.batteriesPresent ?? filledCount;
  const effectiveCharging = raw.batteriesCharging ?? chargingCount;
  const effectiveFull = raw.batteriesFull ?? fullCount;
  // Missing SOC is unknown, not zero. Divide only by readings that actually
  // arrived so one malformed register cannot depress the cabinet average.
  const avgSoc = knownSocCount > 0 ? Math.round(totalSoc / knownSocCount) : 0;

  // Connection status: if disconnected > 30 minutes or reports "hour"/"day", mark offline
  const lastSeen = raw.lastConnectedRaw || raw.last_log_time || '';
  const isOffline = isCabinetOffline(raw.lastConected, lastSeen);

  const cabinetLocation = raw.location ?? raw.cabinetLocation;
  const displayName =
    raw.cabinet_name?.trim() || cabinetLocation?.trim() || raw.serialNumber?.trim() || raw.cabinet_id;

  return {
    cabinet_id: raw.cabinet_id,
    display_name: displayName,
    serial_number: raw.serialNumber,
    cabinetLocation,
    location: cabinetLocation,
    capacity: raw.capacity,
    lat: coords ? coords.lat : null,
    lng: coords ? coords.lng : null,
    total_slots: totalSlots,
    filled_slots: effectiveFilled,
    empty_slots: Math.max(0, totalSlots - effectiveFilled),
    batteries_charging: effectiveCharging,
    batteries_full: effectiveFull,
    avg_soc: avgSoc,
    known_soc_count: knownSocCount,
    last_connected: raw.lastConected || 'Unknown',
    last_connected_raw: lastSeen,
    is_online: !isOffline,
    doors,
    batteries,
    device_status: isOffline ? 1 : 0,
    seen_at: lastSeen || new Date().toISOString(),
  };
}

/** Convert the deployed Rust cabinet feed into the legacy store input shape. */
export function normalizeCabinetRecord(payload: unknown): RawCabinetState | null {
  if (!payload || typeof payload !== 'object') return null;
  const raw = payload as Record<string, unknown>;
  const id = typeof raw.cabinet_id === 'string' ? raw.cabinet_id : null;
  if (!id) return null;
  const rawDoors = Array.isArray(raw.doors) ? raw.doors : [];
  const doors = parseRawCabinetDoors(rawDoors);
  const location =
    typeof raw.location === 'string'
      ? raw.location
      : typeof raw.cabinetLocation === 'string'
        ? raw.cabinetLocation
        : null;
  const cabinetName =
    typeof raw.cabinet_name === 'string'
      ? raw.cabinet_name
      : typeof raw.cabinetName === 'string'
        ? raw.cabinetName
        : null;
  return {
    cabinet_id: id,
    cabinet_name: cabinetName,
    serialNumber: typeof raw.serialNumber === 'string' ? raw.serialNumber : undefined,
    cabinetLocation: location,
    location,
    latitude: (raw.latitude as string | number | null | undefined) ?? null,
    longitude: (raw.longitude as string | number | null | undefined) ?? null,
    capacity: typeof raw.capacity === 'number' ? raw.capacity : null,
    totalSlots: typeof raw.capacity === 'number' ? raw.capacity : rawDoors.length,
    batteriesPresent: doors.filter((d) => d.batteryPresent).length,
    doors,
    last_log_time: typeof raw.last_log_time === 'string' ? raw.last_log_time : null,
    lastConnectedRaw: typeof raw.last_log_time === 'string' ? raw.last_log_time : undefined,
    lastConected: typeof raw.lastConected === 'string' ? raw.lastConected : undefined,
  };
}

let allCabinets = $state<CabinetState[]>([]);
let loading = $state(false);
let error = $state<string | null>(null);
let lastUpdated = $state<number | null>(null);

const withCoords = $derived(allCabinets.filter((c) => c.lat !== null && c.lng !== null));

// Same rule FleetHeat draws its Unserved Charge heat layer against: an
// offline cabinet is not supply (we cannot know what's in it right now), and
// a door only counts once its pack is at FLEET.SOC_GOOD or above — a cabinet
// full of charging packs is not stock yet. Shared here so the reach rings
// (C3) and the site probe (C2/C4) can never disagree with the heatmap about
// what counts as "rideable stock".
const rideableSupply = $derived<SupplyPoint[]>(
  withCoords
    .filter((c) => c.is_online)
    .map((c) => ({
      lat: c.lat as number,
      lng: c.lng as number,
      rideable: c.batteries.filter((b) => !b.empty && (b.soc ?? 0) >= FLEET.SOC_GOOD).length,
    })),
);

const byKey = $derived.by(() => {
  const map: Record<string, CabinetState> = {};
  for (const c of allCabinets) {
    if (c.display_name) map[c.display_name.toLowerCase().trim()] = c;
    if (c.cabinetLocation) map[c.cabinetLocation.toLowerCase().trim()] = c;
    if (c.cabinet_id) map[c.cabinet_id.toLowerCase().trim()] = c;
    if (c.serial_number) map[c.serial_number.toLowerCase().trim()] = c;
  }
  return map;
});

export const cabinets = {
  get all(): CabinetState[] {
    return allCabinets;
  },
  get withCoords(): CabinetState[] {
    return withCoords;
  },
  /** Online cabinets as {@link SupplyPoint}s, `rideable` counting only doors
   *  at FLEET.SOC_GOOD+ — the same shape `fleetModes/soc.ts`'s heat layer,
   *  the reach rings (C3), and `siteProbe()` (C1/C2/C4) all read. */
  get rideableSupply(): SupplyPoint[] {
    return rideableSupply;
  },
  get count(): number {
    return allCabinets.length;
  },
  get withCoordsCount(): number {
    return withCoords.length;
  },
  get loading(): boolean {
    return loading;
  },
  get error(): string | null {
    return error;
  },
  get lastUpdated(): number | null {
    return lastUpdated;
  },

  /**
   * Lookup cabinet by site name, cabinet ID, serial number, or fuzzy matching.
   */
  get(name: string | null | undefined): CabinetState | undefined {
    if (!name) return undefined;
    const clean = name.toLowerCase().trim();
    if (byKey[clean]) return byKey[clean];

    // Normalized alphanumeric fuzzy search
    const norm = normalizeKey(clean);
    if (!norm) return undefined;

    return allCabinets.find((c) => {
      const cLoc = normalizeKey(c.cabinetLocation);
      const cName = normalizeKey(c.display_name);
      const cId = normalizeKey(c.cabinet_id);
      const cSn = normalizeKey(c.serial_number);

      if (
        cLoc &&
        (cLoc === norm ||
          (cLoc.length >= 4 && norm.includes(cLoc)) ||
          (norm.length >= 4 && cLoc.includes(norm)))
      ) {
        return true;
      }
      if (
        cName &&
        (cName === norm ||
          (cName.length >= 4 && norm.includes(cName)) ||
          (norm.length >= 4 && cName.includes(norm)))
      ) {
        return true;
      }
      if (cId && cId === norm) return true;
      if (cSn && cSn === norm) return true;
      return false;
    });
  },

  /** Apply one backend snapshot. Fleet polling calls this so the map and the
   * cabinet store render the exact same response without a duplicate request. */
  ingest(payload: unknown): boolean {
    const rows = normalizeCabinetPayload(payload)
      .map((value) => normalizeCabinetRecord(value))
      .filter((value): value is RawCabinetState => value !== null);
    if (rows.length === 0) return false;
    allCabinets = rows.map(transformRawCabinet).filter((c) => !isHiddenCabinet(c));
    lastUpdated = Date.now();
    error = null;
    loading = false;
    return true;
  },

  async load(): Promise<void> {
    loading = true;
    error = null;

    try {
      const res = await fetch(CABINET_STATE_API);
      if (!res.ok) throw new Error(`Backend cabinet feed HTTP ${res.status}`);
      const payload = (await res.json()) as unknown;
      if (this.ingest(payload)) {
        loading = false;
        return;
      }
    } catch (apiErr) {
      console.warn('Backend cabinet fetch failed:', apiErr);
      error = apiErr instanceof Error ? apiErr.message : String(apiErr);
    }


    // 2. Fallback: query Supabase cabinet_batteries table
    const sb = getSupabase();
    if (sb) {
      try {
        const { data, error: sbErr } = await sb
          .from('cabinet_batteries')
          .select(
            'cabinet_id, display_name, total_slots, filled_slots, empty_slots, avg_soc, batteries, device_status, seen_at',
          );
        if (sbErr) throw sbErr;

        if (Array.isArray(data) && data.length > 0) {
          allCabinets = (data as CabinetBattery[])
            .map((b) => ({
              cabinet_id: b.cabinet_id,
              display_name: b.display_name || b.cabinet_id,
              lat: null,
              lng: null,
              total_slots: b.total_slots ?? 8,
              filled_slots: b.filled_slots ?? 0,
              empty_slots: b.empty_slots ?? 8,
              batteries_charging: 0,
              batteries_full: 0,
              avg_soc: b.avg_soc ?? 0,
              known_soc_count: b.batteries?.filter((battery) => battery.soc != null).length ?? 0,
              last_connected: b.seen_at || '',
              last_connected_raw: b.seen_at || '',
              is_online: b.device_status === 0,
              doors: [],
              batteries: b.batteries ?? [],
              device_status: b.device_status ?? 0,
              seen_at: b.seen_at || '',
            }))
            .filter((c) => !isHiddenCabinet(c));
          lastUpdated = Date.now();
        }
      } catch (fallbackErr) {
        console.error('cabinets.load fallback failed:', fallbackErr);
      }
    }

    loading = false;
  },
};
